import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  InputAdornment,
} from '@mui/material';
import { loyaltyService } from '../services/loyaltyService';

function LoyaltySettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    points_to_euro_rate: '100',
    euro_to_points_rate: '10',
    min_points_redemption: '100',
    max_points_per_order: '50',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await loyaltyService.getAllSettings();
      setSettings(data.settings);
      
      // Remplir le formulaire avec les valeurs actuelles
      const formValues = {};
      data.settings.forEach(setting => {
        formValues[setting.key] = setting.value;
      });
      setFormData(formValues);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des paramètres' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async (key) => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await loyaltyService.updateSetting(key, formData[key]);
      setMessage({ type: 'success', text: 'Paramètre mis à jour avec succès !' });
      await loadSettings();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      for (const setting of settings) {
        await loyaltyService.updateSetting(setting.key, formData[setting.key]);
      }
      setMessage({ type: 'success', text: 'Tous les paramètres ont été mis à jour !' });
      await loadSettings();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  // Calculer des exemples en temps réel
  const pointsToEuroRate = parseFloat(formData.points_to_euro_rate) || 100;
  const euroToPointsRate = parseFloat(formData.euro_to_points_rate) || 10;
  const minPointsRedemption = parseFloat(formData.min_points_redemption) || 100;
  const maxPointsPerOrder = parseFloat(formData.max_points_per_order) || 50;

  const example1000Points = (1000 / pointsToEuroRate).toFixed(2);
  const example100Euros = (100 * euroToPointsRate).toFixed(0);
  const minEuroValue = (minPointsRedemption / pointsToEuroRate).toFixed(2);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          ⭐ Configuration des Points de Fidélité
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Taux de conversion Points → Euros */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  💰 Points → Euros
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Combien de points équivalent à 1 euro
                </Typography>
                
                <TextField
                  fullWidth
                  label="Nombre de points pour 1€"
                  type="number"
                  value={formData.points_to_euro_rate}
                  onChange={(e) => handleChange('points_to_euro_rate', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">points = 1€</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Exemple :</strong> {pointsToEuroRate} points = 1€<br />
                  1000 points = {example1000Points}€
                </Alert>

                <Button
                  variant="contained"
                  onClick={() => handleSave('points_to_euro_rate')}
                  disabled={saving}
                  fullWidth
                >
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Taux de gain Points par Euro dépensé */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  🎁 Gain de Points
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Points gagnés par euro dépensé
                </Typography>
                
                <TextField
                  fullWidth
                  label="Points gagnés par euro"
                  type="number"
                  value={formData.euro_to_points_rate}
                  onChange={(e) => handleChange('euro_to_points_rate', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">points/€</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Exemple :</strong> 1€ dépensé = {euroToPointsRate} points<br />
                  100€ dépensés = {example100Euros} points
                </Alert>

                <Button
                  variant="contained"
                  onClick={() => handleSave('euro_to_points_rate')}
                  disabled={saving}
                  fullWidth
                >
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Minimum de points pour utilisation */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  🔒 Minimum Requis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Minimum de points pour pouvoir les utiliser
                </Typography>
                
                <TextField
                  fullWidth
                  label="Minimum de points"
                  type="number"
                  value={formData.min_points_redemption}
                  onChange={(e) => handleChange('min_points_redemption', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">points min</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Valeur minimum :</strong> {minPointsRedemption} points<br />
                  Équivaut à {minEuroValue}€
                </Alert>

                <Button
                  variant="contained"
                  onClick={() => handleSave('min_points_redemption')}
                  disabled={saving}
                  fullWidth
                >
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Pourcentage maximum par commande */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📊 Maximum par Commande
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  % maximum du total payable en points
                </Typography>
                
                <TextField
                  fullWidth
                  label="Pourcentage maximum"
                  type="number"
                  value={formData.max_points_per_order}
                  onChange={(e) => handleChange('max_points_per_order', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">% max</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 100 }}
                  sx={{ mb: 2 }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Exemple :</strong> Sur une commande de 100€<br />
                  Maximum utilisable = {maxPointsPerOrder}€ en points
                </Alert>

                <Button
                  variant="contained"
                  onClick={() => handleSave('max_points_per_order')}
                  disabled={saving}
                  fullWidth
                >
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Récapitulatif du système */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            📈 Résumé du Système de Fidélité
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#2c2c2c', borderColor: '#444' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#fff' }}>
                  💳 Dépense Client
                </Typography>
                <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                  • 1€ dépensé = <strong>{euroToPointsRate} points</strong> gagnés<br />
                  • 100€ dépensés = <strong>{example100Euros} points</strong> gagnés
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#2c2c2c', borderColor: '#444' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#fff' }}>
                  🎯 Utilisation Points
                </Typography>
                <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                  • <strong>{pointsToEuroRate} points</strong> = 1€ de réduction<br />
                  • 1000 points = <strong>{example1000Points}€</strong> de réduction
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#3a2f1f', borderColor: '#5a4f3f' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#ffc107' }}>
                  ⚠️ Limitations
                </Typography>
                <Typography variant="body2" sx={{ color: '#f5f5dc' }}>
                  • Minimum : <strong>{minPointsRedemption} points</strong> ({minEuroValue}€)<br />
                  • Maximum : <strong>{maxPointsPerOrder}%</strong> du total par commande
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1f3a3a', borderColor: '#2f4a4a' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#4dd0e1' }}>
                  💡 Exemple Pratique
                </Typography>
                <Typography variant="body2" sx={{ color: '#e0f7fa' }}>
                  Commande de 100€ :<br />
                  • Client gagne <strong>{example100Euros} points</strong><br />
                  • Max utilisable : <strong>{maxPointsPerOrder}€</strong> en points ({maxPointsPerOrder * pointsToEuroRate} points)
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSaveAll}
            disabled={saving}
            sx={{ minWidth: 200 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Enregistrer Tous les Paramètres'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default LoyaltySettings;

